/**
 * Browser-only CSV / TSV / clipboard helpers for tantable views.
 *
 * The Excel (.xlsx) workbook path is server-side (ADR-0099 Option B). The
 * `Export to Excel` button on a TanTable widget POSTs the in-memory render
 * model to `POST /tenant/export-table-workbook` and triggers a blob download
 * from the response — see `TanTableViz.handleExport`.
 *
 * What stays here:
 *   - `exportTanTableToCsv` — Blob + anchor download of a .csv file
 *   - `buildTanTableTsv`    — returns a TSV string for clipboard paste
 *
 * These both produce *text*, not workbooks; the download trigger is
 * browser-only and the surface area is small enough that there's no benefit
 * to centralizing it.
 */
import type { ColumnDef } from "@tanstack/react-table";
import type { TanTableRenderModel, TanTableVizSpecV1 } from "./tanTableSpec.js";
import { buildColumnDefs, formatCellValue } from "./tanTableEngine.js";

type RowArray = unknown[];
type VizSpecV1Column = TanTableVizSpecV1["columns"][number];

type ColumnFormatMeta = {
  isNumeric: boolean;
  isDate: boolean;
  colSpec?: VizSpecV1Column;
};

function resolveFormatMeta(model: TanTableRenderModel): ColumnFormatMeta[] {
  const spec = model.tableSpec;
  const colSpecById = new Map(spec.columns.map((c) => [c.id, c]));
  const colSpecByField = new Map(spec.columns.map((c) => [c.field, c]));
  const measureById = new Map((spec.measures ?? []).map((m) => [m.id, m]));
  const measureByField = new Map((spec.measures ?? []).map((m) => [m.field, m]));

  return model.columns.map((col) => {
    let colSpec = colSpecById.get(col.key) ?? colSpecByField.get(col.key);
    let measure = measureById.get(col.key) ?? measureByField.get(col.key);
    if (!measure && col.pivotGroupLabel) {
      for (const m of spec.measures ?? []) {
        if (col.key.endsWith(`_${m.id}`) || col.key.endsWith(`_${m.field}`)) { measure = m; break; }
      }
    }
    if (!colSpec && measure) {
      colSpec = colSpecById.get(measure.id) ?? colSpecByField.get(measure.field);
    }
    const fmt = colSpec?.format ?? measure?.format;
    const dataType = colSpec?.dataType;
    const isNumeric = col.type === "number" || !!measure;
    const isDate = dataType === "date" || dataType === "datetime";
    const resolvedSpec: VizSpecV1Column | undefined = colSpec
      ? (fmt && fmt !== colSpec.format ? { ...colSpec, format: fmt } : colSpec)
      : measure
        ? { id: col.key, field: col.key, dataType, format: fmt } as VizSpecV1Column
        : undefined;
    return { isNumeric, isDate, colSpec: resolvedSpec };
  });
}

function resolveDefHeader(def: ColumnDef<RowArray, unknown>): string {
  const h = def.header;
  if (typeof h === "string") return h;
  if (typeof h === "function") return String(def.id ?? "");
  return String(def.id ?? "");
}

function collectLeafHeaderPaths(
  defs: ColumnDef<RowArray, unknown>[],
  prefix: string[],
  out: Array<{ id: string; path: string[] }>,
): void {
  for (const def of defs) {
    const label = resolveDefHeader(def);
    const childCols = "columns" in def && Array.isArray(def.columns) ? def.columns : null;
    if (childCols && childCols.length > 0) {
      collectLeafHeaderPaths(childCols as ColumnDef<RowArray, unknown>[], [...prefix, label], out);
    } else if (def.id != null) {
      out.push({ id: def.id as string, path: [...prefix, label] });
    }
  }
}

function buildHeaderGrid(leaves: Array<{ id: string; path: string[] }>): {
  grid: string[][];
  maxRows: number;
} {
  const nCols = leaves.length;
  if (nCols === 0) return { grid: [], maxRows: 0 };
  const pathLengths = leaves.map((l) => l.path.length);
  const maxRows = Math.max(...pathLengths);
  const grid: string[][] = Array.from({ length: maxRows }, () => Array(nCols).fill(""));
  for (let j = 0; j < nCols; j++) {
    const P = leaves[j].path;
    for (let r = 0; r < P.length; r++) {
      grid[r][j] = P[r];
    }
  }
  return { grid, maxRows };
}

export function exportTanTableToCsv(
  model: TanTableRenderModel,
  visibleRows: unknown[][],
  grandTotalRow: unknown[] | null,
  filename?: string,
): void {
  const columnDefs = buildColumnDefs(model);
  const leaves: Array<{ id: string; path: string[] }> = [];
  collectLeafHeaderPaths(columnDefs, [], leaves);

  const keyToModelIndex = new Map(model.columns.map((c, i) => [c.key, i]));
  const formatMeta = resolveFormatMeta(model);

  const modelIndexForLeaf = (leafId: string): number => {
    const idx = keyToModelIndex.get(leafId);
    if (idx === undefined) {
      throw new Error(`exportTanTableToCsv: unknown column id "${leafId}"`);
    }
    return idx;
  };

  const renderCell = (raw: unknown, ci: number): unknown => {
    if (raw == null) return raw;
    const meta = formatMeta[ci];
    if (meta?.isDate && meta.colSpec) return formatCellValue(raw, meta.colSpec);
    return raw;
  };

  const rowValuesInLeafOrder = (row: unknown[]): unknown[] =>
    leaves.map((leaf) => {
      const ci = modelIndexForLeaf(leaf.id);
      return renderCell(row[ci], ci);
    });

  const csvEscape = (value: unknown): string => {
    if (value == null) return "";
    const str = String(value);
    if (str.includes(",") || str.includes("\n") || str.includes('"')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const lines: string[] = [];
  const { grid, maxRows } = buildHeaderGrid(leaves);
  for (let r = 0; r < maxRows; r++) {
    lines.push(grid[r].map((cell) => csvEscape(cell || "")).join(","));
  }

  for (const row of visibleRows) {
    lines.push(rowValuesInLeafOrder(row).map(csvEscape).join(","));
  }

  if (grandTotalRow) {
    const ordered = rowValuesInLeafOrder(grandTotalRow).map((v, oi) => {
      if (oi === 0 && v == null) return "Grand Total";
      return v == null ? "" : v;
    });
    lines.push(ordered.map(csvEscape).join(","));
  }

  const csv = lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = (filename ?? "export") + ".csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Builds a TSV string (Excel-paste compatible). Tab is the column separator;
 * newline ends rows. Cells containing tab, newline, or `"` are quoted with
 * doubled-up `"`s — same convention Excel uses on paste.
 */
export function buildTanTableTsv(
  model: TanTableRenderModel,
  visibleRows: unknown[][],
  grandTotalRow: unknown[] | null,
): string {
  const columnDefs = buildColumnDefs(model);
  const leaves: Array<{ id: string; path: string[] }> = [];
  collectLeafHeaderPaths(columnDefs, [], leaves);

  const keyToModelIndex = new Map(model.columns.map((c, i) => [c.key, i]));
  const formatMeta = resolveFormatMeta(model);

  const modelIndexForLeaf = (leafId: string): number => {
    const idx = keyToModelIndex.get(leafId);
    if (idx === undefined) {
      throw new Error(`buildTanTableTsv: unknown column id "${leafId}"`);
    }
    return idx;
  };

  const renderCell = (raw: unknown, ci: number): unknown => {
    if (raw == null) return raw;
    const meta = formatMeta[ci];
    if (meta?.isDate && meta.colSpec) return formatCellValue(raw, meta.colSpec);
    return raw;
  };

  const rowValuesInLeafOrder = (row: unknown[]): unknown[] =>
    leaves.map((leaf) => {
      const ci = modelIndexForLeaf(leaf.id);
      return renderCell(row[ci], ci);
    });

  const tsvEscape = (value: unknown): string => {
    if (value == null) return "";
    const str = String(value);
    if (str.includes("\t") || str.includes("\n") || str.includes("\r") || str.includes('"')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const lines: string[] = [];
  const { grid, maxRows } = buildHeaderGrid(leaves);
  for (let r = 0; r < maxRows; r++) {
    lines.push(grid[r].map((cell) => tsvEscape(cell || "")).join("\t"));
  }

  for (const row of visibleRows) {
    lines.push(rowValuesInLeafOrder(row).map(tsvEscape).join("\t"));
  }

  if (grandTotalRow) {
    const ordered = rowValuesInLeafOrder(grandTotalRow).map((v, oi) => {
      if (oi === 0 && v == null) return "Grand Total";
      return v == null ? "" : v;
    });
    lines.push(ordered.map(tsvEscape).join("\t"));
  }

  return lines.join("\n");
}
